import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { fetchFamiliesList } from '../../redux-store/actions/investorProfile';
import { PaginationObj, SortingObj } from '../investorOverview/types';
import { RootStateType } from '../../redux-store/reducers';
import { USER_ROLES } from '../../utils/constant';
import { Box, Button, Drawer, IconButton, InputAdornment, TextField, Theme, Typography, useTheme } from '@mui/material';
import { DialogCloseIcon, PlusIcon, SearchIcon } from '../../assets/SvgIcons';
import CustomTable from '../investorOverview/common/customTable';
import ViewFamilyTableBody, { viewFamilyTableHeader } from './ViewFamilyTableBody';
import BreadCrum from '../../lib/BreadCrum';
import CustomFilter from '../investorOverview/common/CustomFilter';
import { whiteSpaceOnlyRegex } from '../../utils/regex';
import { FamilyListObj, FamiliesListRes } from '../../redux-store/types/investorProfile';
import { isRMLikeRole } from '../../utils/utilityFunctions';

export default function ViewFamily(): JSX.Element {
    const history = useHistory();
    const dispatch = useDispatch();
    const initialRouter = history.location.pathname?.split('/').filter((item) => item !== '')
    const [familiesListLoading, setFamiliesListLoading] = useState<boolean>(false);
    const [familiesList, setFamiliesList] = useState<Array<FamilyListObj>>([]);



    const [searchText, setSearchText] = useState<string>('');
    const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 10,
        totalRows: 0
    })
    const [totalFilters, setTotalFilters] = useState<number>(0);
    const theme = useTheme();
    const { role = "" } = useSelector((state: RootStateType) => state.auth);
    const { iwappRmId = "" } = useSelector((state: RootStateType) => state.id);
    const [isFilterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false);
    const [familiesListCount, setFamiliesListCount] = useState<number>(0);


    const handleCloseFilterDrawer = () => {
        setFilterDrawerOpen(false);
    };

    const isFiltersLoading = false;
    const handleFiltersClearAll = () => { console.log('filters clear') };
    const handleApplyFilters = () => { console.log('filters apply') };

    const fetchListOfFamilies = async ({
        searchInput = "",
        currentPage = 1,
        rowsPerPage = 10,
    }: {
        searchInput?: string;
        currentPage?: number;
        rowsPerPage?: number;
    }) => {
        try {
            setFamiliesListLoading(true);
            const listOfFamilies = await (dispatch(fetchFamiliesList({
                rmId: `${iwappRmId}`,
                search: searchInput,
                currentPage,
                rowsPerPage
            })) as unknown as Promise<FamiliesListRes>);
            setFamiliesList(listOfFamilies?.result ?? []);
            setFamiliesListCount(listOfFamilies?.result?.length ?? 0);

        } catch (e) {
            console.log('ERROR!', e)
        } finally {
            setFamiliesListLoading(false);
        }
    }

    useEffect(() => {
        fetchListOfFamilies({
            searchInput: "",
            currentPage: tablePaginationObj.currentPage + 1,
            rowsPerPage: tablePaginationObj.rowsPerPage
        });
    }, [])

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            rowsPerPage: Number(event.target.value),
            currentPage: 0,
        }))

        fetchListOfFamilies({
            searchInput: "",
            currentPage: 1,
            rowsPerPage: Number(event.target.value),
        });
    }

    const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: page
        }))

        fetchListOfFamilies({
            searchInput: "",
            currentPage: page + 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage,
        })
    }

    const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (whiteSpaceOnlyRegex.test(event.target.value)) return;
        setSearchText(event.target.value);
    }

    const handleClearSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
        setSearchText("")
        e.preventDefault()
    }

    // Client-side filtering as the API currently only supports pagination and rmId
    const filteredFamiliesList = familiesList.filter(item =>
        item.familyName?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.familyID?.toLowerCase().includes(searchText.toLowerCase()) ||
        item.familyHeadName?.toLowerCase().includes(searchText.toLowerCase())
    );



    const rmRouterPath = [
        { name: 'Dashboard', path: '/dashboard', warning: false },
        { name: 'View Families', path: `/${initialRouter[0]}` },
    ]

    const investorRouterPath = [
        {
            name: 'Dashboard',
            path: '/investor-dashboard',
        },
        {
            name: 'View Families',
            path: `/${initialRouter[0]}`,
        },

    ]

    const handleFamMemCreation = () => {
        if (role === USER_ROLES.INVESTOR) {
            history.push('/investor-family/addFamilyMember', {
                obj: familiesList[0]
            });
        }
        else {
            history.push('/family/createFamily');
        }
    }

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BreadCrum array={isRMLikeRole(role) ? rmRouterPath : investorRouterPath} />
            </Box>
            <Box mb="25px" mt={2}>
                <Typography variant='h1' color="text.primary">
                    {role === USER_ROLES.INVESTOR ? 'Family Status' : 'View Families'}
                </Typography>
            </Box>
            <Box
                sx={(theme: Theme) => ({
                    marginTop: "15.5px",
                    borderRadius: "25px",
                    padding: { xs: "10px 15px", sm: "20px 30px" },
                    backgroundColor: theme.palette.common.white,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                })}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Search Input */}
                    <TextField
                        sx={{
                            width: { xs: '100%', sm: 'auto' },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        sx={{ display: searchText.length > 0 ? 'inline-flex' : 'none' }}
                                        onClick={handleClearSearch} aria-label="Clear search"
                                    >
                                        <DialogCloseIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            sx: (theme: Theme) => ({
                                borderRadius: '10px',
                                height: '42px',
                                border: `1px solid ${theme.palette.text.secondary}B2`,
                                '& fieldset': {
                                    border: 'none',
                                },
                                '& input': {
                                    paddingLeft: '10px',
                                    ...theme.typography.body_500,
                                    color: theme.palette.text.primary,
                                    '::placeholder': {
                                        ...theme.typography.subtitle_400,
                                        color: theme.palette.text.secondary,
                                        opacity: 1,
                                    },
                                },
                            }),
                        }}
                        placeholder="Name, Code and Head"
                        value={searchText}
                        onChange={handleSearchTextChange}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '40px', mt: { xs: '20px', sm: '0px' } }}>

                        {/* Add Family */}
                        <Button sx={(theme: Theme) => ({
                            background: theme.palette.primary[500],
                            borderRadius: '4px',
                            width: '100%',
                            padding: '7px',
                            gap: '5px',
                            '&:hover': {
                                backgroundColor: theme.palette.primary[500]
                            }
                        })}
                            onClick={handleFamMemCreation}>
                            <PlusIcon />
                            <Typography variant='body_500'sx={(theme: Theme) => ({
                                color: theme.palette.textDark.primary,
                            })}>
                                {role === USER_ROLES.INVESTOR ? 'Add Member' : 'Create Family'}
                            </Typography>
                        </Button>
                    </Box>
                </Box>

                {/* View Family Table */}
                <Box sx={{ mt: '35px' }}>
                    <CustomTable
                        isLoading={familiesListLoading}
                        tableBody={
                            <ViewFamilyTableBody data={filteredFamiliesList ?? []} />
                        }
                        tableData={filteredFamiliesList ?? []}
                        tableHeaderData={viewFamilyTableHeader}
                        isPaginationVisible={true}
                        tableDataCount={familiesListCount}
                        handleRowsPerPageChange={handleRowsPerPageChange}
                        handlePageChange={handlePageChange}
                        rowsPerPage={tablePaginationObj.rowsPerPage}
                        currentPage={tablePaginationObj.currentPage}
                        rowsPerPageOptions={[5, 10, 20, 30]}
                        tableBodyLoaderRowCustomStyles={{
                            '& td:first-of-type': {
                                paddingLeft: "0px"
                            },
                        }}
                        tableHeadRowCustomStyles={{
                            '& th': {
                                backgroundColor: theme.palette.textDark.disabled,
                            },
                            '& th:first-of-type': {
                                paddingLeft: "0px",
                            }
                        }}
                        tableBodyCustomStyles={{
                            '& tr:last-child td': {
                                border: 0
                            }
                        }}
                        loadingRowsNum={5}
                    />
                </Box>
            </Box>
            <Drawer
                open={isFilterDrawerOpen}
                onClose={handleCloseFilterDrawer}
                anchor="right"
                PaperProps={{
                    sx: {
                        padding: "25px",
                        width: "450px"
                    }
                }}
                ModalProps={{
                    sx: (theme: Theme) => ({
                        backgroundColor: `${theme.palette.common.black}03`
                    })
                }}
            >
                <CustomFilter
                    isLoading={isFiltersLoading}
                    handleClearAll={handleFiltersClearAll}
                    handleApplyFilters={handleApplyFilters}
                    showCloseIcon={true}
                    showAmcFilter={false}
                    showCategoryFilter={false}
                    showRiskFilter={false}
                    handleCloseIconClick={handleCloseFilterDrawer}
                    defaultExpandedFilter="productType"
                />
            </Drawer>
        </>
    )
}
